import React, { useCallback, useEffect } from 'react';
import './FileViewer.css';

interface FileViewerProps {
  fileName: string;
  content: string;
  onClose: () => void;
}

export const FileViewer: React.FC<FileViewerProps> = ({ fileName, content, onClose }) => {
  const MAX_LINES = 2000;
  const allLines = content.split('\n');
  const isTruncated = allLines.length > MAX_LINES;
  const lines = isTruncated ? allLines.slice(0, MAX_LINES) : allLines;

  const handleDownload = useCallback(() => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }, [content, fileName]);

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="file-viewer-overlay" onClick={handleOverlayClick}>
      <div className="file-viewer-modal" role="dialog" aria-modal="true" aria-label={fileName}>
        <div className="file-viewer-header">
          <span className="file-viewer-title">{fileName}</span>
          <div className="file-viewer-actions">
            <button className="file-viewer-btn" onClick={handleDownload}>
              Download
            </button>
            <button className="file-viewer-btn file-viewer-close" onClick={onClose}>
              &times;
            </button>
          </div>
        </div>
        <div className="file-viewer-content">
          <table className="file-viewer-lines">
            <tbody>
              {lines.map((line, i) => (
                <tr key={i}>
                  <td className="file-viewer-line-num">{i + 1}</td>
                  <td className="file-viewer-line-text">{line}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {isTruncated && (
            <div className="file-viewer-truncated">
              Showing first {MAX_LINES.toLocaleString()} of {allLines.length.toLocaleString()} lines. Download to see the full file.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
